import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Loader2, Megaphone, Plus, Trash2, CheckCircle2, KeyRound, RotateCcw } from "lucide-react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const eur = (n) => `€ ${Number(n || 0).toFixed(2).replace(".", ",")}`;
const linkFor = (code) => `${window.location.origin}/?ref=${code}`;

const Stat = ({ label, value, testid, strong }) => (
  <div>
    <div className="text-[11px] uppercase tracking-wider text-[#8C7F78]">{label}</div>
    <div className={`mt-0.5 ${strong ? "text-[#C87D75] font-semibold" : "text-[#2D2825]"}`} data-testid={testid}>{value}</div>
  </div>
);

const AddForm = ({ onAdded }) => {
  const [f, setF] = useState({ name: "", instagram: "", email: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/admin/influencers", { name: f.name, instagram: f.instagram || null, email: f.email || null });
      navigator.clipboard?.writeText(linkFor(data.code)).catch(() => {});
      toast.success(`${data.name} aggiunta · link copiato`);
      setF({ name: "", instagram: "", email: "" });
      onAdded();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Controlla nome ed email");
    } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="grid sm:grid-cols-[1fr_1fr_1fr_auto] gap-2" data-testid="influencer-add-form">
      <Input placeholder="Nome (es. Giulia Rossi)" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required minLength={2} data-testid="inf-name" />
      <Input placeholder="Instagram (es. @giulia)" value={f.instagram} onChange={(e) => setF({ ...f, instagram: e.target.value })} data-testid="inf-instagram" />
      <Input type="email" placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="inf-email" />
      <Button type="submit" disabled={busy} className="rounded-full bg-[#2D2825] hover:bg-[#473f3a]" data-testid="inf-add">
        {busy ? <Loader2 className="animate-spin" size={16} /> : <><Plus size={16} /> Aggiungi</>}
      </Button>
    </form>
  );
};

const Row = ({ it, onChange }) => {
  const [busy, setBusy] = useState(false);
  const copy = () => navigator.clipboard.writeText(linkFor(it.code)).then(() => toast.success("Link copiato"));
  const portal = `${window.location.origin}/influencer/${it.portal_token}`;
  const copyPortal = () => navigator.clipboard.writeText(portal).then(() => toast.success("Link pagina privata copiato: mandalo solo a lei"));
  const regen = async () => {
    if (!window.confirm("Creare un nuovo link privato? Quello vecchio smetterà di funzionare.")) return;
    try {
      const { data } = await api.post(`/admin/influencers/${it.id}/portal-token`);
      await navigator.clipboard.writeText(`${window.location.origin}/influencer/${data.portal_token}`).catch(() => {});
      toast.success("Nuovo link privato creato e copiato"); onChange();
    } catch { toast.error("Operazione non riuscita"); }
  };
  const pay = async () => {
    if (!window.confirm(`Segnare come pagati ${eur(it.to_pay)} a ${it.name}?`)) return;
    setBusy(true);
    try { const { data } = await api.post(`/admin/influencers/${it.id}/paid`); toast.success(`Segnati come pagati ${eur(data.paid)}`); onChange(); }
    catch { toast.error("Operazione non riuscita"); } finally { setBusy(false); }
  };
  const remove = async () => {
    if (!window.confirm(`Rimuovere ${it.name}? Il suo link smetterà di funzionare.`)) return;
    try { await api.delete(`/admin/influencers/${it.id}`); toast.success(`${it.name} rimossa`); onChange(); }
    catch { toast.error("Rimozione non riuscita"); }
  };
  return (
    <div className="rounded-2xl border border-[#EAE3DC] bg-white p-4" data-testid={`influencer-${it.code}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="font-medium text-[#2D2825]">{it.name}</div>
          <div className="text-xs text-[#8C7F78]">{[it.instagram && `@${it.instagram}`, it.email].filter(Boolean).join(" · ") || "—"}</div>
          <button onClick={copy} className="mt-2 inline-flex items-center gap-1.5 text-xs text-[#4A7496] hover:underline break-all text-left" data-testid={`inf-copy-${it.code}`}>
            <Copy size={13} className="shrink-0" /> {linkFor(it.code)}
          </button>
          <div className="mt-1 flex flex-wrap items-center gap-3 text-xs">
            <button onClick={copyPortal} className="inline-flex items-center gap-1.5 text-[#8E7BA3] hover:underline" data-testid={`inf-portal-copy-${it.code}`}>
              <KeyRound size={13} /> Copia link pagina privata
            </button>
            <button onClick={regen} className="inline-flex items-center gap-1 text-[#8C7F78] hover:text-[#2D2825]" data-testid={`inf-portal-regen-${it.code}`}>
              <RotateCcw size={12} /> Rigenera
            </button>
          </div>
        </div>
        <div className="flex gap-2">
          <Button size="sm" onClick={pay} disabled={busy || !it.to_pay} className="rounded-full bg-[#4E7363] hover:bg-[#3f5f51]" data-testid={`inf-pay-${it.code}`}>
            {busy ? <Loader2 className="animate-spin" size={14} /> : <><CheckCircle2 size={14} /> Segna pagato</>}
          </Button>
          <button onClick={remove} aria-label="Rimuovi" className="text-[#B8ADA5] hover:text-[#B85750] p-2" data-testid={`inf-remove-${it.code}`}><Trash2 size={15} /></button>
        </div>
      </div>
      <div className="mt-4 grid grid-cols-3 sm:grid-cols-6 gap-3 text-sm">
        <Stat label="Visite link" value={it.clicks || 0} testid={`inf-clicks-${it.code}`} />
        <Stat label="Mamme iscritte" value={it.customers} testid={`inf-customers-${it.code}`} />
        <Stat label="Ordini" value={it.orders} testid={`inf-orders-${it.code}`} />
        <Stat label="Venduto" value={eur(it.sales)} testid={`inf-sales-${it.code}`} />
        <Stat label="Già pagato" value={eur(it.paid)} testid={`inf-paid-${it.code}`} />
        <Stat label="Da pagare" value={eur(it.to_pay)} testid={`inf-topay-${it.code}`} strong />
      </div>
      {it.recent?.length > 0 && (
        <div className="mt-3 border-t border-[#F1ECE7] pt-2 text-xs text-[#5C534E] space-y-1">
          {it.recent.map((o) => (
            <div key={o.name} className="flex justify-between gap-2">
              <span>{o.name} · {o.date.split("-").reverse().join("/")}</span>
              <span>{eur(o.subtotal)} → {eur(o.commission)} {o.paid ? "· pagato" : ""}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const InfluencerAdmin = () => {
  const [data, setData] = useState(null);
  const load = useCallback(() => api.get("/admin/influencers").then((r) => setData(r.data)).catch(() => toast.error("Impossibile caricare le influencer")), []);
  useEffect(() => { load(); }, [load]);

  return (
    <section className="rounded-2xl border border-[#EAE3DC] bg-[#FDFBF9] p-5 sm:p-6 space-y-4" data-testid="influencer-admin">
      <div className="flex items-center gap-2">
        <Megaphone size={18} className="text-[#C87D75]" />
        <h3 className="font-medium text-[#2D2825]">Influencer</h3>
      </div>
      <p className="text-sm text-[#5C534E]">
        Ogni influencer ha un link personale. Chi lo apre viene ricordata per 30 giorni su quel dispositivo:
        sugli acquisti fatti dall'app riconosci all'influencer il {data?.rate ?? 5}% del totale prodotti (senza spedizione, sconti già tolti).
      </p>
      <AddForm onAdded={load} />
      {data?.warning && <div className="text-xs text-[#B07A3B]" data-testid="inf-warning">{data.warning}</div>}
      {!data ? <Loader2 className="animate-spin text-[#8C7F78]" size={18} /> : data.items.length === 0 ? (
        <div className="text-sm text-[#8C7F78]" data-testid="inf-empty">Nessuna influencer ancora. Aggiungi la prima qui sopra.</div>
      ) : (
        <div className="space-y-3">{data.items.map((it) => <Row key={it.id} it={it} onChange={load} />)}</div>
      )}
    </section>
  );
};
